import React from 'react';
import { motion } from 'framer-motion';
import { useAudioContext } from '@/contexts/AudioContext';
import { LayoutManager } from '@/components/layout/LayoutManager';
import { PlaylistArea } from '@/components/layout/PlaylistArea';
import { VisualizerArea } from '@/components/layout/VisualizerArea';
import { StreamerPlayer } from '@/components/StreamerPlayer';
import { Player } from '@/components/Player';
import { AppHeader } from '@/components/AppHeader';

function MainLayoutContent({ layoutDimensions, isLayoutReady }) {
  const {
    // UI State
    isPlayerMinimized,
    setIsPlayerMinimized,
    visualizerType,
    setVisualizerType,
    isPlaylistCollapsed,
    setIsPlaylistCollapsed,
    togglePlaylist,
    isStreamerMode,
    lyricsPosition, 
    
    // Audio refs
    analyserRef,
    dataArrayRef,
    
    // Audio player state
    currentSong,
    isPlaying,
    currentTime,
    duration,
    volume,
    isLooping,
    isPlaylistLooping,
    seek,
    setVolume,
    toggleLoop,
    togglePlaylistLoop,
    
    // Playlist state
    playlist,
    currentIndex,
    totalPlaylistDuration, 
    removeSong,
    reorderSongs,
    playNext,
    playPrevious,
    playSongAtIndex,
    
    // Handlers
    addSongs, 
    handlePlayPause,
    forcePlay,
    forcePause, 

    // Normalization
    normalizationApplied,
    showNormalizationIndicator,

    // O2P Mode
    isO2PMode
  } = useAudioContext();

  console.log("MainLayout.jsx: totalPlaylistDuration from context:", totalPlaylistDuration);


  const playerHeight = isPlayerMinimized ? (isStreamerMode ? 60 : 80) : (isStreamerMode ? 100 : 128);
  
  const playlistWidth = isStreamerMode ? 0 : (isPlaylistCollapsed ? 48 : 320);
  const visualizerHeight = Math.max(0, layoutDimensions.height - playerHeight);
  const visualizerWidth = Math.max(0, layoutDimensions.width - playlistWidth);

  const containerBgClass = isStreamerMode 
    ? 'bg-black' 
    : 'bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900';

  return (
    <>
      <div 
        className={`flex h-full overflow-hidden transition-all duration-300 ${containerBgClass}`}
        style={{ position: 'relative' }}
      >
        <PlaylistArea
          playlist={playlist}
          currentIndex={currentIndex}
          isPlaying={isPlaying}
          isPlaylistCollapsed={isPlaylistCollapsed}
          setIsPlaylistCollapsed={setIsPlaylistCollapsed}
          isStreamerMode={isStreamerMode}
          onFileUpload={addSongs} 
          playSongAtIndex={playSongAtIndex}
          removeSong={removeSong}
          reorderSongs={reorderSongs}
          handlePlayPause={handlePlayPause}
          forcePlay={forcePlay}
          forcePause={forcePause}
          totalPlaylistDuration={totalPlaylistDuration} 
        />

        <div 
          className="flex flex-col flex-1 min-w-0 min-h-0 overflow-hidden"
          style={{ 
            width: visualizerWidth,
            height: layoutDimensions.height,
            minWidth: visualizerWidth,
            minHeight: layoutDimensions.height
          }}
        >
          <VisualizerArea
            layoutDimensions={layoutDimensions}
            isLayoutReady={isLayoutReady}
            visualizerWidth={visualizerWidth}
            visualizerHeight={visualizerHeight}
            isStreamerMode={isStreamerMode}
            analyserRef={analyserRef}
            dataArrayRef={dataArrayRef}
            isPlaying={isPlaying}
            visualizerType={visualizerType}
            setVisualizerType={setVisualizerType}
            currentSong={currentSong}
            currentTime={currentTime}
            lyricsPosition={lyricsPosition} 
          />

          <div 
            className={`border-t transition-all duration-300 ease-in-out overflow-hidden ${
              isStreamerMode 
                ? 'bg-black border-white/5' 
                : 'bg-black/30 backdrop-blur-sm border-white/10'
            }`}
            style={{ 
              height: playerHeight,
              minHeight: playerHeight,
              maxHeight: playerHeight,
              width: visualizerWidth,
              minWidth: visualizerWidth,
              maxWidth: visualizerWidth
            }}
          >
            <motion.div 
              initial={{ y: 100, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="w-full h-full"
            >
              {isStreamerMode ? (
                <StreamerPlayer
                  currentSong={currentSong}
                  isPlaying={isPlaying}
                  currentTime={currentTime}
                  duration={duration}
                  volume={volume}
                  isLooping={isLooping}
                  isPlaylistLooping={isPlaylistLooping}
                  isMinimized={isPlayerMinimized}
                  playlist={playlist}
                  currentIndex={currentIndex}
                  isPlaylistCollapsed={isPlaylistCollapsed}
                  onPlayPause={handlePlayPause} 
                  onSeek={seek}
                  onVolumeChange={setVolume}
                  onToggleLoop={toggleLoop}
                  onTogglePlaylistLoop={togglePlaylistLoop}
                  onNext={playNext}
                  onPrevious={playPrevious}
                  onToggleMinimize={() => setIsPlayerMinimized(!isPlayerMinimized)}
                  onTogglePlaylist={togglePlaylist}
                  onFileUpload={addSongs} 
                  onSongSelect={playSongAtIndex}
                  onSongRemove={removeSong}
                  onReorder={reorderSongs}
                  normalizationApplied={normalizationApplied}
                  showNormalizationIndicator={showNormalizationIndicator}
                  visualizerType={visualizerType}
                  setVisualizerType={setVisualizerType}
                />
              ) : (
                <Player
                  currentSong={currentSong}
                  isPlaying={isPlaying}
                  currentTime={currentTime}
                  duration={duration}
                  volume={volume}
                  isLooping={isLooping}
                  isPlaylistLooping={isPlaylistLooping}
                  isMinimized={isPlayerMinimized}
                  isStreamerMode={isStreamerMode}
                  handlePlayPause={handlePlayPause} 
                  onSeek={seek}
                  onVolumeChange={setVolume}
                  onToggleLoop={toggleLoop}
                  onTogglePlaylistLoop={togglePlaylistLoop}
                  onNext={playNext}
                  onPrevious={playPrevious}
                  onToggleMinimize={() => setIsPlayerMinimized(!isPlayerMinimized)}
                  normalizationApplied={normalizationApplied}
                  showNormalizationIndicator={showNormalizationIndicator}
                />
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </>
  );
}

export function MainLayout() {
  return (
    <LayoutManager>
      <MainLayoutContent />
    </LayoutManager>
  );
}